// #191 — Manual-acknowledge dialog for SEND_PAYMENT_LINK cells when the
// practice has no Stripe Connect, or when the user explicitly picks
// "Mark as paid (no link)" from the Stripe-enabled cell's arrow menu.
//
// Pure presentation: parent owns "open", confirms by calling onConfirm with
// integer pence + optional note. Money never leaves this component as a float.
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useResponsive } from 'src/hooks';

export default function PaymentManualAcknowledgeDialog({
    open,
    onClose,
    treatmentName,
    amountInputDefault,
    onConfirm,
    sending,
}) {
    const [amount, setAmount] = useState(amountInputDefault || '');
    const [note, setNote] = useState('');
    const isMobile = useResponsive('down', 'sm');

    // I4 fix: parseFloat accepts scientific notation, leading whitespace, and
    // sub-pence fractions ("1.234"). Validate strict pounds.pence format via
    // regex so we never round nonsense values into integer pence.
    const POUNDS_PENCE_RE = /^\d+(\.\d{1,2})?$/;
    const trimmedAmount = (amount || '').trim();
    const matchesFormat = POUNDS_PENCE_RE.test(trimmedAmount);
    const parsedPounds = matchesFormat ? parseFloat(trimmedAmount) : NaN;
    const isValid = matchesFormat && Number.isFinite(parsedPounds) && parsedPounds > 0;
    const amountPence = isValid ? Math.round(parsedPounds * 100) : 0;
    const showFormatError = !matchesFormat && trimmedAmount.length > 0;
    const formattedPrompt = isValid
        ? `Has the payment of £${parsedPounds.toFixed(2)} been received?`
        : 'Has the payment been received?';

    const handleConfirm = () => {
        if (!isValid) return;
        onConfirm({ amountPence, note: note.trim() || undefined });
    };

    const handleClose = () => {
        if (sending) return;
        setAmount(amountInputDefault || '');
        setNote('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ fontSize: '1rem', pb: 1 }}>Mark as paid</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                        Treatment: <strong>{treatmentName}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Amount (£)"
                        type="number"
                        inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal' }}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        error={showFormatError}
                        helperText={showFormatError ? 'Enter a positive amount with up to 2 decimal places (e.g. 49.99).' : ' '}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                        {formattedPrompt}
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        label="Notes (optional)"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={sending}
                    sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!isValid || sending}
                    sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}
                >
                    {sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

PaymentManualAcknowledgeDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentName: PropTypes.string,
    amountInputDefault: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    sending: PropTypes.bool,
};

PaymentManualAcknowledgeDialog.defaultProps = {
    treatmentName: 'this treatment',
    amountInputDefault: '',
    sending: false,
};
